/**
 * EventStreamContext — Shared SSE connection provider
 *
 * Provides a single EventSource connection to the entire app.
 * Components subscribe to channels via useEventStreamSubscription hook.
 *
 * Usage:
 *   // In App.js or layout
 *   <EventStreamProvider>
 *     <App />
 *   </EventStreamProvider>
 *
 *   // In any component
 *   const { connected, subscribe } = useEventStreamContext();
 *   
 *   useEffect(() => {
 *     return subscribe('notifications', (event) => {
 *       console.log('Notification:', event);
 *     });
 *   }, [subscribe]);
 */

import React, { createContext, useContext, useMemo } from "react";
import useEventStream, { CHANNELS } from "hooks/useEventStream";

const EventStreamContext = createContext(null);

export function EventStreamProvider({ children }) {
  const eventStream = useEventStream();

  return (
    <EventStreamContext.Provider value={eventStream}>
      {children}
    </EventStreamContext.Provider>
  );
}

/**
 * Hook to access the shared event stream context
 */
export function useEventStreamContext() {
  const context = useContext(EventStreamContext);
  if (!context) {
    throw new Error("useEventStreamContext must be used within EventStreamProvider");
  }
  return context;
}

/**
 * Convenience hook to subscribe to a specific channel
 *
 * Usage:
 *   useEventStreamSubscription('notifications', (event) => {
 *     console.log('Notification:', event);
 *   });
 */
export function useEventStreamSubscription(channel, callback) {
  const { subscribe } = useEventStreamContext();

  React.useEffect(() => {
    if (!callback) return;
    return subscribe(channel, callback);
  }, [subscribe, channel, callback]);
}

// Re-export channels for convenience
export { CHANNELS };

export default EventStreamContext;
